// CCS1: Cleo can take a full-state capture of the Desk at any time, including
// what lies below the fold. The harness asks by appending one activity on the
// calling thread; the Desk showing that session answers with its image and the
// complete text of every open pane over the loopback delivery route.
import * as Schema from "effect/Schema";
import { IsoDateTime } from "./baseSchemas.ts";
import { ChatImageAttachment } from "./orchestration.ts";

export const DESK_VIEW_CAPTURE_ACTIVITY_KIND = "desk-view.capture.requested";
export const DESK_VIEW_CAPTURE_SCHEMA_VERSION = "desk-view-capture-v1";

export const DeskViewCaptureRequestPayload = Schema.Struct({
  requestId: Schema.String,
  worldKey: Schema.NullOr(Schema.String),
  requestedAt: IsoDateTime,
  includeImage: Schema.Boolean,
});
export type DeskViewCaptureRequestPayload = typeof DeskViewCaptureRequestPayload.Type;

export const DeskViewCapturePane = Schema.Struct({
  /** Landmark or role of the pane (main, nav, aside, tabpanel, region, dialog). */
  kind: Schema.String,
  title: Schema.String,
  /** The pane's complete rendered text, scrolled-away content included. */
  text: Schema.String,
  textTruncated: Schema.Boolean,
});
export type DeskViewCapturePane = typeof DeskViewCapturePane.Type;

export const DeskViewCaptureImage = Schema.Struct({
  mediaType: Schema.Literal("image/png"),
  base64: Schema.String,
  width: Schema.Number,
  height: Schema.Number,
});
export type DeskViewCaptureImage = typeof DeskViewCaptureImage.Type;

export const DeskViewCapture = Schema.Struct({
  schemaVersion: Schema.Literal(DESK_VIEW_CAPTURE_SCHEMA_VERSION),
  requestId: Schema.String,
  worldKey: Schema.NullOr(Schema.String),
  route: Schema.String,
  title: Schema.String,
  capturedAt: IsoDateTime,
  viewport: Schema.Struct({ width: Schema.Number, height: Schema.Number }),
  image: Schema.NullOr(DeskViewCaptureImage),
  panes: Schema.Array(DeskViewCapturePane),
  /** Honest limits of this capture (virtualized rows, no image, and so on). */
  notes: Schema.Array(Schema.String),
});
export type DeskViewCapture = typeof DeskViewCapture.Type;

export const DeskViewCaptureDelivery = Schema.Struct({
  requestId: Schema.String,
  capture: DeskViewCapture,
});
export type DeskViewCaptureDelivery = typeof DeskViewCaptureDelivery.Type;

/**
 * What capture_desk_view hands the model: the Desk's text, and the picture as
 * a retained image the lane attaches as an image (never base64 text). The
 * Desk delivers the full-resolution PNG above; the server shrinks it to a
 * bounded JPEG, retains it in the conversation's attachment store, and
 * answers with this descriptor only (W-59e53401).
 */
export const DeskViewCaptureResult = Schema.Struct({
  schemaVersion: Schema.Literal(DESK_VIEW_CAPTURE_SCHEMA_VERSION),
  requestId: Schema.String,
  worldKey: Schema.NullOr(Schema.String),
  route: Schema.String,
  title: Schema.String,
  capturedAt: IsoDateTime,
  viewport: Schema.Struct({ width: Schema.Number, height: Schema.Number }),
  /** The retained picture; null when none was asked for or it could not be prepared. */
  image: Schema.NullOr(ChatImageAttachment),
  /** The picture's size in pixels after it was shrunk for the model. */
  imageSize: Schema.NullOr(Schema.Struct({ width: Schema.Number, height: Schema.Number })),
  panes: Schema.Array(DeskViewCapturePane),
  notes: Schema.Array(Schema.String),
});
export type DeskViewCaptureResult = typeof DeskViewCaptureResult.Type;
